import React, { useState, useEffect } from "react";
import { X, Calendar as CalendarIcon, CheckCircle } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, startOfWeek, endOfWeek, isSameMonth } from "date-fns";
import { getAttendanceRecords } from "../../services/attendance";
import { AttendanceRecord } from "../../types/attendance";
import toast from "react-hot-toast";

interface AttendanceCalendarModalProps {
  userId: string;
  userName: string;
  userType: string;
  onClose: () => void;
}

const AttendanceCalendarModal: React.FC<AttendanceCalendarModalProps> = ({
  userId,
  userName,
  userType,
  onClose,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAttendance();
  }, [currentMonth, userId]);

  const loadAttendance = async () => {
    try {
      setLoading(true);
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);

      const response = await getAttendanceRecords({
        userId,
        dateFrom: format(monthStart, "yyyy-MM-dd"),
        dateTo: format(monthEnd, "yyyy-MM-dd"),
        page: 1,
        limit: 100,
      });

      setAttendanceRecords(response.data || []);
    } catch (error) {
      toast.error("Failed to load attendance data");
      console.error("Error loading attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const getAttendanceForDate = (date: Date) => {
    return attendanceRecords.find((record) => {
      const recordDate = new Date(record.date);
      return isSameDay(recordDate, date);
    });
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-md sm:max-w-lg w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-3 sm:p-4 rounded-t-xl sm:rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Attendance Calendar
              </h2>
              <p className="text-blue-100 text-xs sm:text-sm mt-1 truncate">
                {userName} • {userType.charAt(0).toUpperCase() + userType.slice(1)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Calendar Navigation */}
        <div className="p-3 sm:p-4 border-b border-slate-200">
          <div className="flex justify-between items-center gap-2">
            <button
              onClick={previousMonth}
              className="px-2 sm:px-3 py-1.5 sm:py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs sm:text-sm font-semibold text-slate-700 transition-colors"
            >
              ←
            </button>
            <h3 className="text-sm sm:text-lg font-bold text-slate-900">
              {format(currentMonth, "MMM yyyy")}
            </h3>
            <button
              onClick={nextMonth}
              className="px-2 sm:px-3 py-1.5 sm:py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs sm:text-sm font-semibold text-slate-700 transition-colors"
            >
              →
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="p-3 sm:p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-blue-500"></div>
            </div>
          ) : (
            <>
              {/* Week day headers */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
                {weekDays.map((day) => (
                  <div
                    key={day}
                    className="text-center font-bold text-slate-600 text-xs py-1"
                  >
                    {day.slice(0, 1)}
                  </div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {calendarDays.map((day, index) => {
                  const attendance = getAttendanceForDate(day);
                  const isCurrentMonth = isSameMonth(day, currentMonth);
                  const isToday = isSameDay(day, new Date());
                  const hasAttendance = attendance && (attendance.status === "present" || attendance.status === "late");

                  return (
                    <div
                      key={index}
                      className={`
                        relative aspect-square rounded-lg border transition-all duration-200
                        ${!isCurrentMonth ? "bg-slate-50 opacity-40" : "bg-white"}
                        ${isToday ? "border-blue-500 ring-2 ring-blue-200" : "border-slate-200"}
                        ${hasAttendance ? "bg-gradient-to-br from-green-50 to-emerald-100 border-green-400" : ""}
                      `}
                    >
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-0.5">
                        <span
                          className={`text-xs font-semibold ${
                            hasAttendance
                              ? "text-slate-900"
                              : isToday
                              ? "text-blue-600"
                              : isCurrentMonth
                              ? "text-slate-700"
                              : "text-slate-400"
                          }`}
                        >
                          {format(day, "d")}
                        </span>
                        {hasAttendance && (
                          <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary Stats */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <h4 className="font-semibold text-slate-900 text-xs sm:text-sm mb-2">
                  {format(currentMonth, "MMM yyyy")} Summary:
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-green-50 rounded-lg p-2 sm:p-3 border border-green-200">
                    <p className="text-lg sm:text-xl font-bold text-green-700">
                      {attendanceRecords.filter((r) => r.status === "present" || r.status === "late").length}
                    </p>
                    <p className="text-[10px] sm:text-xs text-green-600 font-medium mt-0.5">
                      Days Attended
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-2 sm:p-3 border border-blue-200">
                    <p className="text-lg sm:text-xl font-bold text-blue-700">
                      {attendanceRecords.length > 0
                        ? (
                            (attendanceRecords.filter(
                              (r) => r.status === "present" || r.status === "late"
                            ).length /
                              attendanceRecords.length) *
                            100
                          ).toFixed(0)
                        : 0}
                      %
                    </p>
                    <p className="text-[10px] sm:text-xs text-blue-600 font-medium mt-0.5">
                      Attendance Rate
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-50 p-3 rounded-b-xl sm:rounded-b-2xl border-t border-slate-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm font-semibold rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceCalendarModal;
